
import React, { useState, useEffect } from 'react';
import { View, Coin } from './types.ts';
import { fetchTopCoins } from './services/cryptoApi.ts';
import Home from './components/Home.tsx';
import Markets from './components/Markets.tsx';
import Orders from './components/Orders.tsx';
import Portfolio from './components/Portfolio.tsx';
import CoinDetail from './components/CoinDetail.tsx';
import Navbar from './components/Navbar.tsx';

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<View>('home');
  const [coins, setCoins] = useState<Coin[]>([]);
  const [selectedCoin, setSelectedCoin] = useState<Coin | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    const data = await fetchTopCoins();
    if (data && data.length > 0) {
      setCoins(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 8000); // 8s refresh for highly dynamic feel
    return () => clearInterval(interval);
  }, []);

  const handleCoinClick = (coin: Coin) => {
    setSelectedCoin(coin);
    setCurrentView('coin-detail');
  };

  const renderView = () => {
    if (loading && coins.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full space-y-6">
           <div className="w-14 h-14 border-4 border-blue-500/10 border-t-blue-500 rounded-full animate-spin"></div>
           <p className="text-zinc-500 text-[10px] font-bold tracking-[0.3em] uppercase animate-pulse">Goodbit Syncing</p>
        </div>
      );
    }

    switch (currentView) {
      case 'home':
        return <Home coins={coins} onCoinClick={handleCoinClick} />;
      case 'markets':
        return <Markets coins={coins} onCoinClick={handleCoinClick} />;
      case 'orders':
        return <Orders />;
      case 'portfolio':
        return <Portfolio coins={coins} />;
      case 'coin-detail':
        return selectedCoin ? (
          <CoinDetail 
            coin={selectedCoin} 
            onBack={() => setCurrentView('home')} 
          />
        ) : <Home coins={coins} onCoinClick={handleCoinClick} />;
      default:
        return <Home coins={coins} onCoinClick={handleCoinClick} />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black flex justify-center items-center overflow-hidden">
      {/* Root Mobile Container: Edge to Edge with Safe Areas */}
      <div className="w-full h-full max-w-lg relative bg-[#0D0D0D] flex flex-col overflow-hidden shadow-2xl">
        
        {/* Main Content Viewport */}
        <div className="flex-1 overflow-y-auto hide-scrollbar touch-pan-y pt-safe pb-24">
          {renderView()}
        </div>
        
        {/* Persistent Bottom UI Layer */}
        {currentView !== 'coin-detail' && (
          <div className="absolute bottom-0 left-0 right-0 p-6 pointer-events-none z-50 bg-gradient-to-t from-black via-black/90 to-transparent">
             <div className="pointer-events-auto active-scale">
                <Navbar currentView={currentView} setView={setCurrentView} />
             </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
